import React, { type HTMLInputTypeAttribute } from 'react';
import { Controller, type RegisterOptions, useFormContext } from 'react-hook-form';
import { Flex, Box, Input } from '@chakra-ui/react';
import { Field } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { defaultCountries, PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

type FormFieldInterface = {
	name: string;
	label?: string | React.ReactNode;
	isRequired?: boolean;
	helperText?: string | React.ReactNode;
};

export type FormInputProps = FormFieldInterface & {
	registerOptions?: Partial<RegisterOptions>;
	type?: HTMLInputTypeAttribute;
	inputProps?: React.ComponentProps<typeof Input>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fieldRootProps?: any;
	children?: React.ReactNode;
	min?: number | null;
	max?: number | null;
	maxLength?: number;
	labelProps?: React.ComponentProps<typeof Field.Label>;
	helperTextProps?: React.ComponentProps<typeof Field.HelperText>;
	disabled?: boolean;
	showCharacterCount?: boolean;
	isAutocompleteAllowed?: boolean;
};

export const FormInput: React.FC<FormInputProps> = props => {
	const {
		type = 'text',
		name,
		label,
		isRequired,
		registerOptions,
		inputProps,
		fieldRootProps,
		helperText,
		children,
		min,
		max,
		maxLength,
		labelProps = {},
		helperTextProps = {},
		disabled = false,
		showCharacterCount = false,
		isAutocompleteAllowed = false,
	} = props;
	const { t } = useTranslation();
	const { control } = useFormContext();

	const countries = defaultCountries
		.map(country => {
			const [name, iso2, dialCode, , priority, areaCodes] = country;
			if (iso2 === 'fi') {
				const newFormat = '. .. ..... ...';
				return [name, iso2, dialCode, newFormat, priority, areaCodes];
			}

			return country;
		})
		.filter(Boolean);

	return (
		<Controller
			control={control}
			name={name}
			rules={{
				...(isRequired && { required: t('general.required') }),
				...(typeof min === 'number' && {
					min: {
						value: min,
						message: t('forms.minimum_value_is', { val: min }),
					},
				}),
				...(typeof max === 'number' && {
					max: {
						value: max,
						message: t('forms.maximum_value_is', { val: max }),
					},
				}),

				validate: val => (type === 'text' && val && val.length > 0 ? val.trim().length > 0 || t('general.required') : true),
				...registerOptions,
			}}
			render={({ field, fieldState }) => {
				const value = typeof field.value === 'string' || typeof field.value === 'number' ? field.value : '';
				return (
					<Field.Root invalid={fieldState.invalid} required={isRequired} width={'auto'} {...fieldRootProps}>
						{label && (
							<Field.Label htmlFor={name} {...labelProps}>
								{label}
								<Field.RequiredIndicator />
							</Field.Label>
						)}

						{type === 'tel' ? (
							<PhoneInput
								aria-label={name}
								placeholder={inputProps?.placeholder || ''}
								defaultCountry="fi"
								value={value as string}
								onChange={phoneValue => {
									if (!phoneValue) {
										field.onChange('');
										return;
									}

									// Ensure the value starts with a plus
									const formattedValue = phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`;
									field.onChange(formattedValue);
								}}
								disabled={disabled}
								inputStyle={{
									...inputProps?.style,
									height: '40px',
									borderColor: 'var(--tav-colors-border)',
									opacity: disabled ? '0.5' : 1,
									width: '100%',
								}}
								inputProps={{
									name: 'phone',
									maxLength: maxLength ?? 20,
								}}
								defaultMask="..............."
								style={{ width: '100%' }}
								preferredCountries={['fi', 'se']}
								disableFormatting={true}
								// @ts-ignore
								countries={countries}
							/>
						) : (
							<Input
								aria-label={name}
								id={name}
								type={type}
								value={value}
								onChange={inputValue => {
									if (type === 'number') {
										field.onChange(inputValue.target.value === '' ? '' : Number(inputValue.target.value));
										return;
									}

									field.onChange(inputValue);
								}}
								onBlur={field.onBlur}
								ref={field.ref}
								autoComplete={isAutocompleteAllowed ? name : 'off'}
								name={name}
								maxLength={maxLength}
								disabled={disabled}
								min={min ?? undefined}
								max={max ?? undefined}
								{...inputProps}
							/>
						)}
						{children}

						{(helperText || showCharacterCount) && (
							<Field.HelperText {...helperTextProps}>
								<Flex justify="space-between" gap={3}>
									<Box>{helperText}</Box>
									{showCharacterCount && maxLength && typeof value === 'string' && (
										<Box>
											{value.length} / {maxLength}
										</Box>
									)}
								</Flex>
							</Field.HelperText>
						)}
						<Field.ErrorText id="errorText">{fieldState.error?.message}</Field.ErrorText>
					</Field.Root>
				);
			}}
		/>
	);
};
